import { WEBINAR_ERROR_CODES } from '../constants/webinar.constants'
import type {
  WebinarCredential,
  WebinarRecord,
  WebinarStatus,
} from '../models/webinar.model'
import { webinarCredentialsRepo, webinarRepo } from './webinar.repo'
import {
  WebinarError,
  WebinarValidationError,
  type WebinarValidationErrors,
} from './webinar-error'
import {
  generateWebinarId,
  validateWebinarStep,
  type StepIndex,
} from '../utils/webinar.validators'
import {
  generatePassword as cryptoGeneratePassword,
  generateSalt,
  hashPassword,
  verifyPassword,
} from '@/features/meetings/services/password.crypto'
import { simulateLatency } from '@/shared/utils'
import type { WebinarFormData } from '../steps/types'

/**
 * Webinar service — the only module any UI/state code should reach into.
 * Owns:
 *   - per-step validation orchestration
 *   - record creation, mutation, deletion (delegates to webinar.repo)
 *   - credential management (delegates hashing to password.crypto from meetings)
 *   - join-time gating (existence, status, password)
 *
 * The mock backend is API-shape so replacement is a single-file change.
 */

export function resolveWebinarStatus(startMs: number, endMs: number): WebinarStatus {
  const now = Date.now()
  if (now < startMs) return 'upcoming'
  if (now >= startMs && now <= endMs) return 'ongoing'
  return 'completed'
}

const throwIfErrors = (errors: WebinarValidationErrors) => {
  if (Object.keys(errors).length > 0) throw new WebinarValidationError(errors)
}

const buildRecordFromForm = (
  id: string,
  data: WebinarFormData,
  hostId: string | undefined,
  status: WebinarStatus,
): WebinarRecord => {
  const date = data.date ? new Date(data.date) : new Date()
  const [hh, mm] = (data.time || '09:00').split(':').map(Number)
  date.setHours(hh, mm, 0, 0)
  const startTimeMs = date.getTime()
  const duration = data.durationHrs * 60 + data.durationMins
  const endTimeMs = startTimeMs + duration * 60_000
  const now = Date.now()

  return {
    id,
    title: data.title.trim(),
    description: data.description,
    startTimeMs,
    endTimeMs,
    duration,
    durationHrs: data.durationHrs,
    durationMins: data.durationMins,
    date: date.toISOString().split('T')[0],
    time: data.time,
    timezone: data.timezone,
    additionalTimezones: data.additionalTimezones,
    hostName: data.hostName,
    hostEmail: data.hostEmail,
    hostId,
    invitees: data.invitees,
    maxRegistrants: data.maxRegistrants,
    registrants: [],
    passwordEnabled: data.passwordEnabled,
    password: '', // plaintext lives in the credential record only
    anyTimeJoin: data.anyTimeJoin,
    recording: data.recording,
    status,
    recurring: data.recurring,
    registration: {
      enabled: data.withRegistration,
      fields: data.registrationFields,
      domainRestrictionEnabled: data.domainRestrictionEnabled,
      domainRestrictionType: data.domainRestrictionType,
      authorizedDomains: data.authorizedDomains,
      restrictedDomains: data.restrictedDomains,
      automaticMailApproval: data.automaticMailApproval,
      registrationLimit: data.registrationLimit,
      bannerUrl: data.bannerUrl,
    },
    notifications: {
      reminders: data.reminders,
      postWebinar: data.postWebinar,
    },
    branding: {
      theme: data.webinarTheme,
      logoUrl: data.webinarLogo,
      backgroundUrl: data.webinarBackground,
      introVideoUrl: data.webinarIntroVideo,
    },
    engagement: {
      chat: data.chat,
      chatVisibility: data.chatVisibility,
      liveQandA: data.liveQandA,
      reactions: data.reactions,
      pollsEnabled: data.pollsEnabled,
      resources: data.resources,
      surveysEnabled: data.surveysEnabled,
    },
    security: {
      passwordEnabled: data.passwordEnabled,
      allowJoinAnytime: data.anyTimeJoin,
      recording: data.recording,
    },
    speakers: data.speakers.map((s) => ({
      id: s.id,
      name: s.speakerName,
      email: s.speakerEmail,
      title: s.title,
      organization: s.organization,
      description: s.description,
      imageUrl: s.speakerImage,
      linkedinUrl: s.linkedinUrl,
      facebookUrl: s.facebookUrl,
      twitterUrl: s.twitterUrl,
      websiteUrl: s.websiteUrl,
    })),
    polls: data.polls,
    survey: data.survey,
    createdAt: now,
    updatedAt: now,
  }
}

const setPassword = async (
  webinarId: string,
  plaintext: string,
): Promise<WebinarCredential> => {
  const salt = generateSalt()
  const passwordHash = await hashPassword(plaintext, salt)
  const cred: WebinarCredential = {
    webinarId,
    passwordHash,
    salt,
    plaintext, // mock only
    updatedAt: Date.now(),
  }
  webinarCredentialsRepo.put(cred)
  return cred
}

export const webinarsService = {
  list(): WebinarRecord[] {
    return webinarRepo.all().map((w) => ({
      ...w,
      status:
        w.status === 'draft' || w.status === 'cancelled'
          ? w.status
          : resolveWebinarStatus(w.startTimeMs, w.endTimeMs),
    }))
  },

  getById(id: string): WebinarRecord | undefined {
    const w = webinarRepo.findById(id)
    if (!w) return undefined
    return {
      ...w,
      status:
        w.status === 'draft' || w.status === 'cancelled'
          ? w.status
          : resolveWebinarStatus(w.startTimeMs, w.endTimeMs),
    }
  },

  refreshStatuses(): WebinarRecord[] {
    const updated = webinarRepo.all().map((w) => ({
      ...w,
      status:
        w.status === 'draft' || w.status === 'cancelled'
          ? w.status
          : resolveWebinarStatus(w.startTimeMs, w.endTimeMs),
    }))
    webinarRepo.bulkReplace(updated)
    return updated
  },

  /** Per-step validation. Throws WebinarValidationError if anything fails. */
  validateStep(step: StepIndex, data: WebinarFormData): void {
    const errors = validateWebinarStep(step, data)
    throwIfErrors(errors)
  },

  /**
   * Validate every step. Used when finishing from a non-final step ("Skip and
   * finish") or saving a draft. Returns the merged errors per field.
   */
  validateAll(data: WebinarFormData): WebinarValidationErrors {
    const merged: WebinarValidationErrors = {}
    ;([0, 1, 2, 3, 4, 5, 6, 7] as StepIndex[]).forEach((s) => {
      Object.assign(merged, validateWebinarStep(s, data))
    })
    return merged
  },

  async create(
    data: WebinarFormData,
    hostId: string | undefined,
    status: WebinarStatus = 'upcoming',
  ): Promise<WebinarRecord> {
    // Step 0 is mandatory for non-drafts.
    if (status !== 'draft') {
      this.validateStep(0, data)
    }

    // Mock backend round-trip — keeps the FullPageDotsLoader visible.
    await simulateLatency()

    const id = generateWebinarId()
    const record = buildRecordFromForm(id, data, hostId, status)
    webinarRepo.insert(record)

    if (data.passwordEnabled) {
      const plaintext = (data.password ?? '').trim() || cryptoGeneratePassword()
      await setPassword(id, plaintext)
    }
    return record
  },

  async update(
    id: string,
    data: WebinarFormData,
    hostId: string | undefined,
    status: WebinarStatus = 'upcoming',
  ): Promise<WebinarRecord> {
    const existing = webinarRepo.findById(id)
    if (!existing) throw new WebinarError(WEBINAR_ERROR_CODES.WEBINAR_NOT_FOUND)
    if (status !== 'draft') this.validateStep(0, data)

    await simulateLatency()

    const next = buildRecordFromForm(id, data, hostId, status)
    const updated = webinarRepo.update(id, {
      ...next,
      createdAt: existing.createdAt,
      registrants: existing.registrants,
    })!

    if (data.passwordEnabled) {
      if (data.password && data.password.trim().length > 0) {
        await setPassword(id, data.password.trim())
      } else if (!webinarCredentialsRepo.get(id)) {
        await setPassword(id, cryptoGeneratePassword())
      }
    } else {
      webinarCredentialsRepo.remove(id)
    }
    return updated
  },

  /** Verify a participant can join. Mirrors meetingsService.validateJoin. */
  async validateJoin(input: {
    webinarId: string
    password?: string
  }): Promise<WebinarRecord> {
    await simulateLatency()

    const w = webinarRepo.findById(input.webinarId)
    if (!w) throw new WebinarError(WEBINAR_ERROR_CODES.WEBINAR_NOT_FOUND)

    const status =
      w.status === 'draft' || w.status === 'cancelled'
        ? w.status
        : resolveWebinarStatus(w.startTimeMs, w.endTimeMs)

    if (status === 'cancelled')
      throw new WebinarError(WEBINAR_ERROR_CODES.WEBINAR_CANCELLED)
    if (status === 'completed')
      throw new WebinarError(WEBINAR_ERROR_CODES.WEBINAR_COMPLETED)

    if (w.passwordEnabled) {
      const credential = webinarCredentialsRepo.get(w.id)
      if (credential) {
        if (!input.password) {
          throw new WebinarError(WEBINAR_ERROR_CODES.PASSWORD_REQUIRED)
        }
        const ok = await verifyPassword(
          input.password,
          credential.salt,
          credential.passwordHash,
        )
        if (!ok) throw new WebinarError(WEBINAR_ERROR_CODES.INVALID_PASSWORD)
      }
    }
    return { ...w, status }
  },

  /** Host-only retrieval of the plaintext password (for sharing in details). */
  getPasswordForHost(
    webinarId: string,
    hostId: string | undefined,
    hostEmail?: string,
  ): string | undefined {
    const w = webinarRepo.findById(webinarId)
    if (!w) return undefined
    const isHost =
      (hostId && w.hostId && w.hostId === hostId) ||
      (hostEmail && w.hostEmail && w.hostEmail === hostEmail)
    if (!isHost) return undefined
    const cred = webinarCredentialsRepo.get(w.id)
    return cred?.plaintext
  },

  remove(id: string): boolean {
    return webinarRepo.remove(id)
  },

  generatePassword(): string {
    return cryptoGeneratePassword()
  },
}
